import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ContactsInfo } from 'src/app/custom/common';
import { CookieConsentService } from 'src/app/custom/cookie-consent/cookie-consent.service';
import { buildInfo } from 'src/build-info';
import { environment } from 'src/environments/environment';
import { LayoutFooterComponentImports } from '../..';

@Component({
    standalone: true,
    imports: LayoutFooterComponentImports,
    selector: 'vsf-layout-footer',
    template: `
        <footer aria-labelledby="footer-heading" class="pt-8 bg-second" *transloco="let t; prefix: 'footer'">
            <h2 class="sr-only" id="footer-heading">Footer</h2>
            <div class="sm:max-w-[700px] xl:max-w-[1600px] mx-auto py-8 px-10 sm:px-6 lg:py-6 lg:px-0">
                <div class="sm:grid sm:grid-cols-2 xl:grid-cols-5 sm:space-y-1 space-y-10 gap-16">
                    <!-- logo -->
                    <div class="sm:col-span-2 xl:col-span-1 flex xl:pl-6">
                        <img [src]="logoSrc" alt="Company Logo" [class]="logoClass" />
                    </div>
                    <!-- EINKAUFEN -->
                    <div>
                        <h3 class="text-sm font-semibold text-white tracking-wider uppercase">{{ t('shopping') }}</h3>
                        <ul class="mt-2 sm:space-y-2 space-y-1" role="list">
                            <li>
                                <a [routerLink]="[ContactsInfo.Transport]" class="text-base text-gray-300 hover:text-white">
                                    {{ t('transportOptions') }}
                                </a>
                            </li>
                            <li>
                                <a [routerLink]="[ContactsInfo.Payment]" class="text-base text-gray-300 hover:text-white">
                                    {{ t('paymentOptions') }}
                                </a>
                            </li>
                            <li>
                                <a [routerLink]="[ContactsInfo.Faq]" class="text-base text-gray-300 hover:text-white"> {{ t('faq') }} </a>
                            </li>
                        </ul>
                    </div>

                    <!-- KONTAKT -->
                    <div>
                        <h3 class="text-sm font-semibold text-white tracking-wider uppercase">{{ t('contact') }}</h3>
                        <ul class="mt-2 sm:space-y-2 space-y-1" role="list">
                            <li>
                                <a [href]="contact.phoneHref" class="text-base text-gray-300 hover:text-white">
                                    {{ 'seller.contact.phone' | transloco }}
                                </a>
                            </li>
                            <li>
                                <a 
                                    [href]="'mailto:' + ('seller.contact.email' | transloco)" 
                                    target="_blank"
                                    class="text-base text-gray-300 hover:text-white"
                                >
                                    <h1>{{ 'seller.contact.email' | transloco }}</h1>
                                </a>
                            </li>
                            <li>
                                <a
                                    [href]="contact.addressMapUrl"
                                    target="_blank"
                                    class="text-base text-gray-300 hover:text-white"
                                >
                                    <h1>{{ 'seller.contact.address' | transloco }}</h1>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <!-- ARBEITSZEIT -->
                    <div>
                        <h3 class="text-sm font-semibold text-white tracking-wider uppercase">{{ t('workingHours') }}</h3>
                        <ul class="mt-2 sm:space-y-2 space-y-1" role="list">
                            <li class="text-base text-gray-300">{{ t('days.mo') }} - {{ t('days.fr') }}: 09:00 - 18:00 {{ t('hours') }}</li>
                            <li class="text-base text-gray-300">{{ t('days.sa') }} - {{ t('days.su') }}: {{ t('closed') }}</li>
                        </ul>
                    </div>

                    <!-- INFORMATION -->
                    <div>
                        <h3 class="text-sm font-semibold text-white tracking-wider uppercase">{{ t('information') }}</h3>
                        <ul class="mt-2 sm:space-y-2 space-y-1" role="list">
                            <li>
                                <a [routerLink]="[ContactsInfo.Impressum]" class="text-base text-gray-300 hover:text-white">
                                    {{ t('legalNotice') }}
                                </a>
                            </li>
                            <li>
                                <a [routerLink]="[ContactsInfo.Datenschutzerklaerung]" class="text-base text-gray-300 hover:text-white">
                                    {{ t('policy') }}
                                </a>
                            </li>
                            <li>
                                <a href="/api/shared/legal/AGB.pdf" target="_blank" class="text-base text-gray-300 hover:text-white">
                                    <h1>{{ t('termsAndConditions') }}</h1>
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/api/shared/legal/Widerrufsbelehrung.pdf"
                                    target="_blank"
                                    class="text-base text-gray-300 hover:text-white"
                                >
                                    <h1>{{ t('cancellationPolicy') }}</h1>
                                </a>
                            </li>
                            <li>
                                <a
                                    href="/api/shared/legal/Muster-Widerrufsformular.pdf"
                                    target="_blank"
                                    class="text-base text-gray-300 hover:text-white"
                                >
                                    <h1>{{ t('cancellationForm') }}</h1>
                                </a>
                            </li>
                            <li>
                                <a [href]="accessibilityPdf" target="_blank" class="text-base text-gray-300 hover:text-white">
                                    <h1>{{ t('accessibility') }}</h1>
                                </a>
                            </li>
                            <li>
                                <button
                                    class="text-base text-gray-300 hover:text-white"
                                    (click)="openCookies()"
                                    aria-label="Open cookie settings"
                                >
                                    {{ t('cookies') }}
                                </button>
                            </li>
                            <li>
                                <a [routerLink]="[ContactsInfo.Company]" class="text-base text-gray-300 hover:text-white">
                                    {{ t('companyInformation') }}
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div class="hidden xl:flex xl:pl-6 items-end">
                        <span
                            class="text-xs font-normal normal-case tracking-normal text-second hover:text-white transition-colors select-text"
                            [title]="buildVersionLabel"
                            [attr.aria-label]="buildVersionLabel"
                        >
                            {{ buildVersionLabel }}
                        </span>
                    </div>

                    <div class="text-sm font-semibold text-white tracking-wider uppercase sm:col-span-1 xl:col-span-2">
                        {{ t('shippingProviders') }}
                        <div class="mt-3 flex items-center gap-3 flex-wrap">
                            <p-image src="assets/icons/dhl_logo.png" alt="DHL" class="h-10"></p-image>
                            <p-image src="assets/icons/dpd_logo.png" alt="DPD" class="h-10"></p-image>
                        </div>
                    </div>
                    <div class="text-sm font-semibold text-white tracking-wider uppercase sm:col-span-1 xl:col-span-2">
                        {{ t('paymentMethods') }}
                        <div class="mt-3 flex items-center gap-3 flex-wrap">
                            <p-image src="assets/icons/visa_logo.png" alt="Visa" class="h-10"></p-image>
                            <p-image src="assets/icons/mastercard_logo.png" alt="Master Card" class="h-10"></p-image>
                            <p-image src="assets/icons/googlePay_logo.png" alt="Google Pay" class="h-10"></p-image>
                            <p-image src="assets/icons/applePay_logo.png" alt="Apple Pay" class="h-10"></p-image>
                            <p-image src="assets/icons/amazonPay_logo.png" alt="Amazon Pay" class="h-10"></p-image>
                            <p-image src="assets/icons/PayPal_logo.png" alt="PayPal" class="h-10 pt-[6px]"></p-image>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LayoutFooterComponent {
    private cookieService = inject(CookieConsentService);
    ContactsInfo = ContactsInfo;
    readonly logoSrc = environment.logoSrc;
    readonly logoClass = environment.logoClass;
    readonly contact = environment.contact;
    readonly accessibilityPdf = environment.legal.accessibilityPdf;
    readonly buildVersionLabel = [
        `v${buildInfo.version}`,
        buildInfo.commitShortSha,
        buildInfo.commitTimestamp,
    ].filter(Boolean).join(' ');

    openCookies() {
        this.cookieService.openCookieSettings();
    }
}
